import { Box, Button, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { settingsAtom } from '../../atoms/Settings';
import { useThemeColor } from '../../hooks/useThemeColor';
import { Blog } from '../../interfaces/Blog/Blog';
import { getBlogs } from '../../api/Blog';
import { BlogCard } from './BlogCard';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useLocalizedText } from '../../hooks/useLocalizedText';

export const Blogs = () => {
  const t = useLocalizedText();

  const settings = useRecoilValue(settingsAtom);

  const [blogs, setBlogs] = useState<Blog[]>([]);

  const navigate = useNavigate();

  const { primary, secondary, ternary } = useThemeColor();
  const isMobile = useIsMobile();

  useEffect(() => {
    getBlogs()
      .then((b) => setBlogs(b))
      .catch((err) => toast.error(err.message));
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        p: isMobile ? 3 : 10,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'stretch',
        gap: 2.5,
        backgroundColor: settings.settingsV1.storefront_blogs_color,
        boxSizing: 'border-box',
        ...settings.settingsV2?.blogsWidgetSettings?.rootStyle,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            color: ternary,
            fontSize: '36px',
            fontWeight: 600,
            ...settings.settingsV2?.blogsWidgetSettings?.titleStyle,
          }}
        >
          {settings.settingsV1.blog_text}
        </Typography>

        <Typography
          sx={{
            color: ternary,
            width: 250,
            fontSize: '16px',
            fontWeight: 500,
            ...settings.settingsV2?.blogsWidgetSettings?.subtitleStyle,
          }}
        >
          {settings.settingsV1.blog_description ||
            t('blog.placeholder_subtitle')}
        </Typography>

        <Button
          onClick={() => navigate('/blog')}
          sx={{
            fontWeight: 700,
            fontSize: '13px',
            padding: '8px 15px',
            color: secondary,
            backgroundColor: primary,
            transition: 'background-color 0.3s ease',
            textTransform: 'none',
            width: 'fit-content',
            '&:hover': {
              backgroundColor: ternary,
            },
            ...settings.settingsV2?.blogsWidgetSettings?.viewAllButtonStyle,
          }}
        >
          {t('blog.view_all_contents')}
        </Button>
      </Box>

      <Box sx={{ ml: 'auto' }} />

      {isMobile ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gap: 2.5,
            overflowX: 'auto',
            alignItems: 'stretch', // ensures children stretch vertically
          }}
        >
          {blogs.slice(0, 3).map((blog, i) => (
            <Box key={i} sx={{ minWidth: 250, display: 'flex' }}>
              <BlogCard blog={blog} sx={{ flex: 1 }} />
            </Box>
          ))}
        </Box>
      ) : (
        blogs.slice(0, 3).map((blog, i) => <BlogCard key={i} blog={blog} />)
      )}
    </Box>
  );
};
